import {
  CheckboxTreeView,
  TreeItemData,
} from '@aibees/elephant/components/checkbox-tree-view';
import { LOG_API_CALL_DURATION_ENABLE } from '@aibees/elephant/constants';
import { useTreeViewSetters } from '@aibees/elephant/context/tree-view';
import {
  Classification,
  useOptionsUrl,
} from '@aibees/elephant/feature/search-filters';
import { useAutocompleteSearchQuery } from '@aibees/elephant/feature/search-filters/filters/autocomplete-tree-filter/hooks/use-autocomplete-search-query';
import { useFilteredTreeItems } from '@aibees/elephant/feature/search-filters/filters/autocomplete-tree-filter/hooks/use-filtered-tree-items';
import { useGetTreeItems } from '@aibees/elephant/feature/search-filters/filters/autocomplete-tree-filter/hooks/use-get-tree-items';
import { useIsSearchQueryReachedMinimumLengthThreshold } from '@aibees/elephant/feature/search-filters/filters/autocomplete-tree-filter/hooks/use-is-search-query-reached-minimum-length-threshold';
import { useLoadFilterDataApi } from '@aibees/elephant/feature/search-filters/hooks/use-load-filter-api';
import {
  renderInput,
  renderTags,
} from '@aibees/elephant/feature/search-filters/utils/render-utils';
import { MetadataItem, useSearchQueryMetadata } from '@aibees/elephant/hooks';
import { Grid2, Icon, Scrollbar } from '@aibees/shared/components';
import { SearchProvider } from '@aibees/shared/context';
import { KeyCodes } from '@aibees/shared/definition';
import {
  useBoolean,
  useHandleClickOutside,
  useStableReference,
} from '@aibees/shared/hooks';
import { classnames, peformanceLogTime } from '@aibees/shared/utils';
import {
  Box,
  Card,
  CardContent,
  OutlinedInputProps,
  Typography,
  useTheme,
} from '@mui/material';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useJobTitleExpanderStore } from '../../store';
import * as Styled from './styles';
import * as SX from './sx';
import type { SelectedTitlesInputProps as Props } from './types';

// TODO: @mehdi: refactor this component based on the new autocomplete filter component > ask Maziar to describe the changes

export const SelectedTitlesInput = memo(
  ({
    className,
    metadata,
    treeOptions,
    config,
    name,
    field,
    utils,
    open: filterOpen,
    tabs,
    options,
    searchKey,
    icon: filterIcon,
    onClear,
  }: Props) => {
    const { selectedValues, setSelectedValues, clearData } =
      useJobTitleExpanderStore();

    const _setTreeViewSelectedValues = useTreeViewSetters();
    const open = useBoolean(false);
    const theme = useTheme();
    const { searchInputValue, setSearchInputValue, debouncedSearchQuery } =
      useAutocompleteSearchQuery();
    const [focused, setFocused] = useState(false);

    const isSearchQueryReachedMinimumLengthThreshold =
      useIsSearchQueryReachedMinimumLengthThreshold(debouncedSearchQuery);

    const componentRef = useRef<HTMLDivElement>(null);
    useHandleClickOutside(
      componentRef,
      () => {
        setFocused(false);
        // open.onFalse();
      },
      0
    );
    useEffect(() => {
      _setTreeViewSelectedValues(selectedValues);
    }, []);

    // options api
    const url = useOptionsUrl(options, 0, tabs);
    const isGettingDataFromApi = !!url;
    const optionsApi = useLoadFilterDataApi<Classification>(
      url!,
      debouncedSearchQuery && !metadata?.autocomplete?.onlySearchInResults
        ? {
            [searchKey || (config?.labelKey as string)]: debouncedSearchQuery,
          }
        : {},
      utils,
      0,
      options,
      metadata?.autocomplete?.tabsCountBadge,
      tabs
    );

    const loading = !!url && !!optionsApi && optionsApi.isLoading;

    // metadata api
    const metadataApi = useSearchQueryMetadata(
      metadata?.metaDataApi?.key,
      !!(filterOpen && metadata?.metaDataApi?.enabled)
    );

    const metadataApiData = useStableReference(
      metadataApi.data,
      (metadataData) =>
        JSON.stringify(
          metadataData?.map((item: MetadataItem) => item.value) ?? []
        )
    );

    const treeItems: TreeViewBaseItem<TreeItemData>[] = useMemo(() => {
      return treeOptions?.[0] ?? [];
    }, [treeOptions]);

    const { items } = useGetTreeItems(
      config,
      metadata,
      utils,
      filterIcon,
      treeItems,
      undefined,
      metadataApiData,
      0,
      selectedValues,
      metadata?.autocomplete?.multiple
    );

    const filteredTreeItems = useFilteredTreeItems({
      items,
      debouncedSearchQuery,
      isGettingDataFromApi,
      metadata,
      customOptionsLimit: undefined,
    });

    function onChangeSelection(
      selectedItems: string[],
      _selectedValue?: string
    ) {
      if (LOG_API_CALL_DURATION_ENABLE)
        peformanceLogTime(`filter change start: ${field}`);
      if (!selectedItems.length) {
        clearData();
      }

      if (metadata?.autocomplete?.multiple) {
        setSelectedValues(selectedItems);
      } else {
        setSearchInputValue('');
        open.onFalse();
      }

      if (LOG_API_CALL_DURATION_ENABLE)
        peformanceLogTime(`filter change end: ${field}`);
    }
    const handleDeleteChip = (option: string) => {
      const newSelectedValues = selectedValues.filter((v) => v !== option);

      if (onClear) {
        onClear(option);
      } else {
        setSelectedValues(newSelectedValues);
        _setTreeViewSelectedValues(newSelectedValues);
      }

      if (!newSelectedValues.length) {
        clearData();
      }
    };

    const handleKeyDown: OutlinedInputProps['onKeyDown'] = (e) => {
      if (e.key === KeyCodes.backspace) {
        e.stopPropagation();
      }

      if (
        metadata?.autocomplete?.allowCustomValuesByEnter &&
        e.key === KeyCodes.enter &&
        searchInputValue.length > 0
      ) {
        if (metadata?.autocomplete?.multiple) {
          onChangeSelection(
            [...selectedValues, searchInputValue],
            searchInputValue
          );
        } else {
          onChangeSelection([searchInputValue], searchInputValue);
        }
        setSearchInputValue('');
      }
    };
    const handleSearch = (searchedValue: string) => {
      const transformer = metadata?.autocomplete?.transformSearchValue;
      const v = transformer ? transformer(searchedValue) : searchedValue;
      setSearchInputValue(v);
    };

    const handleClickChevron = () => {
      open.onToggle();
      setSearchInputValue('');
    };

    // ============ renders ============

    const renderedTags = renderTags({
      tagsList: selectedValues,
      utils,
      metadata,
      onDelete: (e, v) => handleDeleteChip(v),
    });

    const renderedInput = renderInput({
      field: field as string,
      search: searchInputValue,
      onChange: (v) => handleSearch(v),
      onFocus: () => {
        setFocused(true);
        open.onTrue();
      },
      onKeyDown: handleKeyDown,
      placeholder: metadata?.placeholder,
    });

    return (
      <Styled.Wrapper
        className={classnames('SelectedTitlesInput', className)}
        sx={SX.root}
        display={'flex'}
        flexDirection={'column'}
        gap={0.5}
        ref={componentRef}
      >
        <Card variant="outlined" color={focused ? 'primary' : 'default'}>
          <CardContent padding="normal">
            <Grid2 container columns={48} display={'flex'}>
              <Grid2 xs={42} display={'flex'} flexDirection={'column'}>
                {!!selectedValues.length && (
                  <CardContent>{renderedTags}</CardContent>
                )}
                {renderedInput}
              </Grid2>
              <Grid2
                xs={6}
                display={'flex'}
                alignItems={'start'}
                justifyContent={'center'}
                paddingTop={1}
              >
                <Box sx={SX.chevronBox} onClick={handleClickChevron}>
                  <Icon
                    name={open.value ? 'chevron-up' : 'chevron-down'}
                    color={focused ? 'primary' : 'grey.500'}
                    size={16}
                  />
                </Box>
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>

        {open.value && (
          <Card variant="outlined" color={focused ? 'primary' : 'default'}>
            <CardContent padding="normal">
              <Box
                height={
                  filteredTreeItems.length === 0 ? theme.spacing(18) : 'none'
                }
              >
                <Scrollbar sx={SX.scrollbar}>
                  {loading ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      gap={1}
                      height={theme.spacing(18)}
                      width="100%"
                    >
                      <Icon name="hourglass-empty" size={18} color="grey.400" />
                      <Typography variant="body5" color="grey.500">
                        Loading...
                      </Typography>
                    </Box>
                  ) : (
                    <SearchProvider>
                      {filteredTreeItems.length > 0 ? (
                        <CheckboxTreeView
                          items={items}
                          filteredItems={filteredTreeItems}
                          onChange={onChangeSelection}
                          search={debouncedSearchQuery}
                          multiSelect={metadata?.autocomplete?.multiple}
                          checkboxPosition={
                            metadata?.autocomplete?.checkboxPosition
                          }
                          metadataEnabled={metadata?.metaDataApi?.enabled}
                          metadataApiLoading={metadataApi.isFetching}
                          iconSX={metadata?.autocomplete?.iconSx}
                          hideLogoFallback={
                            metadata?.autocomplete?.hideLogoFallback
                          }
                          isSearchQueryReachedMinimumLengthThreshold={
                            isSearchQueryReachedMinimumLengthThreshold
                          }
                        />
                      ) : metadata?.emptyMessage ? (
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          gap={1}
                          height={theme.spacing(18)}
                          width="100%"
                        >
                          <Typography variant="body4" color="grey.500">
                            {metadata.emptyMessage}
                          </Typography>
                        </Box>
                      ) : null}
                    </SearchProvider>
                  )}
                </Scrollbar>
              </Box>
            </CardContent>
          </Card>
        )}
      </Styled.Wrapper>
    );
  }
);

SelectedTitlesInput.displayName = 'SelectedTitlesInput';
